import { WorkflowData } from "../WorkflowEditor";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";

interface GuidanceTabProps {
  data: WorkflowData;
  onChange: (data: WorkflowData) => void;
}

export function GuidanceTab({ data, onChange }: GuidanceTabProps) {
  function addSuccessCriteria() {
    onChange({ ...data, success_criteria: [...data.success_criteria, ""] });
  }

  function updateSuccessCriteria(index: number, value: string) {
    const newCriteria = [...data.success_criteria];
    newCriteria[index] = value;
    onChange({ ...data, success_criteria: newCriteria });
  }

  function removeSuccessCriteria(index: number) {
    const newCriteria = data.success_criteria.filter((_, i) => i !== index);
    onChange({ ...data, success_criteria: newCriteria });
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold">Workflow Guidance</h2>
        <p className="text-sm text-muted-foreground">
          Provide instructions and success criteria to help users complete the workflow
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="guidance_notes">Overall Guidance Notes</Label>
        <Textarea
          id="guidance_notes"
          value={data.guidance_notes}
          onChange={(e) => onChange({ ...data, guidance_notes: e.target.value })}
          placeholder="Provide general guidance for completing this workflow. This will be shown to users in a help panel..."
          rows={8}
        />
        <p className="text-xs text-muted-foreground">
          Use markdown for formatting. This appears in the guidance panel.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label>Success Criteria</Label>
            <p className="text-xs text-muted-foreground">
              What defines successful completion of this workflow?
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={addSuccessCriteria}>
            <Plus className="h-3 w-3 mr-1" />
            Add Criteria
          </Button>
        </div>

        <div className="space-y-2">
          {data.success_criteria.map((criteria, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={criteria}
                onChange={(e) => updateSuccessCriteria(index, e.target.value)}
                placeholder="e.g., All required documents generated and signed"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeSuccessCriteria(index)}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
          {data.success_criteria.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No success criteria defined yet
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Frequently Asked Questions</Label>
        <Textarea
          placeholder="Add common questions and answers here..."
          rows={6}
        />
        <p className="text-xs text-muted-foreground">
          Coming soon: Structured FAQ builder
        </p>
      </div>

      <div className="space-y-2">
        <Label>Related Resources</Label>
        <Textarea
          placeholder="Links to related training videos, knowledge base articles, etc..."
          rows={4}
        />
        <p className="text-xs text-muted-foreground">
          Coming soon: Resource link manager
        </p>
      </div>
    </div>
  );
}